// src/util/appAnalytics.js
/**
 * 이용 행동 로그(2026-10-19) — 화면·클릭·스크롤·이탈·유입을 우리 서버(POST /analytics/app-events)에 모은다.
 *  외부 분석 도구로 보내지 않는다. 개인정보처리방침 §5(화면 방문·클릭 기록 자동수집) 범위.
 *
 *  식별: aid = 이 기기·브라우저의 익명 ID(localStorage), sid = 세션(30분 동안 아무 일 없으면 새로).
 *   앱 안 웹페이지면 네이티브가 넣어 준 window.__muyeonAnalytics 의 aid·sid 를 그대로 쓴다 → 앱 화면과 한 세션.
 *  전송: 모아서 10초·20건마다. 화면을 떠나거나 앱이 내려갈 때는 fetch keepalive(페이지가 닫혀도 전송).
 *   sendBeacon 은 로그인 헤더를 못 실어 같은 사람이 회원·기기로 두 번 세졌다 → 헤더를 실을 수 있는 keepalive 를 먼저 쓴다.
 *  이벤트 이름·키는 iOS·AOS 와 같다(backend app-analytics.normalize.ts CLIENT_EVENTS).
 *
 *  ★ 로그 때문에 화면이 실패하면 안 된다 — 모든 진입점은 예외를 삼킨다.
 */
import { callPostAppEvents } from "api/MuyeonApi";
import { detectRuntime, Runtime } from "util/userAgent";
import { getLandingUtm, getLandingType } from "util/landingParams";
import { tokenStore } from "store/tokenStore";

const ENDPOINT = "/analytics/app-events";
const AID_KEY = "muyeon.aid";
const SID_KEY = "muyeon.sid";
const PENDING_KEY = "muyeon.analytics.pending";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const FLUSH_MS = 10 * 1000;
const FLUSH_AT = 20;
const PENDING_MAX = 200;

const uuid = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (e) { /* noop */ }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};
const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* noop */ } },
};

/** 네이티브가 넣어 주는 값(앱 1.0.2 / versionCode 17 부터). 없으면 null. */
const nativeCtx = () => {
  const n = typeof window !== "undefined" ? window.__muyeonAnalytics : null;
  return n && typeof n.aid === "string" ? n : null;
};

export const getAid = () => {
  const n = nativeCtx();
  if (n) return n.aid;
  let aid = store.get(AID_KEY);
  if (!aid || !/^[A-Za-z0-9-]{8,64}$/.test(aid)) {
    aid = uuid();
    store.set(AID_KEY, aid);
  }
  return aid;
};

/** 세션 — 30분 동안 아무 이벤트가 없었으면 새 세션. 앱 안이면 앱의 세션. */
const getSid = () => {
  const n = nativeCtx();
  if (n?.sid) return n.sid;
  const now = Date.now();
  let cur = null;
  try { cur = JSON.parse(store.get(SID_KEY) || "null"); } catch (e) { cur = null; }
  const sid = cur && cur.id && now - cur.at < SESSION_IDLE_MS ? cur.id : uuid();
  store.set(SID_KEY, JSON.stringify({ id: sid, at: now }));
  return sid;
};

const osVersion = () => {
  const ua = navigator.userAgent || "";
  const ios = ua.match(/OS (\d+)[_.](\d+)/);
  if (/iPhone|iPad|iPod/i.test(ua) && ios) return `${ios[1]}.${ios[2]}`;
  const android = ua.match(/Android (\d+(?:\.\d+)?)/);
  return android ? android[1] : null;
};

const platformInfo = () => {
  const n = nativeCtx();
  if (n) return { platform: n.platform, surface: "WEBVIEW", appVersion: n.appVersion, osVersion: n.osVersion, deviceModel: n.deviceModel };
  const rt = detectRuntime();
  // 구버전 앱(값을 안 넣어 줌)도 앱 안이면 앱 플랫폼으로 센다.
  if (rt === Runtime.IOS_WEBVIEW) return { platform: "IOS", surface: "WEBVIEW", osVersion: osVersion() };
  if (rt === Runtime.ANDROID_WEBVIEW) return { platform: "AOS", surface: "WEBVIEW", osVersion: osVersion() };
  return { platform: "WEB", surface: "BROWSER", osVersion: osVersion() };
};

const utmCtx = () => {
  const u = getLandingUtm();
  const out = {};
  // id = Meta 광고 ID(utm_id={{ad.id}}) — 서버가 props.adId 로 남긴다(2026-10-24).
  ["source", "medium", "campaign", "content", "id"].forEach((k) => {
    const v = u[`utm_${k}`];
    if (typeof v === "string" && v) out[k] = v.slice(0, 100);
  });
  return out;
};

// webdriver = 자동 조종 브라우저(검토 로봇 등)가 스스로 알리는 표시 — 서버가 봇으로 표시해 통계에서 뺀다(2026-10-24).
const isWebdriver = () => {
  try { return navigator.webdriver === true; } catch (e) { return false; }
};

// lp = 광고 종류(?lp=) — 서버가 이벤트마다 props.lp 로 붙여 광고 퍼널을 나눈다(2026-10-21).
const ctx = () => ({
  aid: getAid(), sid: getSid(), ...platformInfo(), utm: utmCtx(), lp: getLandingType() || undefined,
  webdriver: isWebdriver() || undefined,
});

let queue = [];
let timer = null;

const readPending = () => {
  try { return JSON.parse(store.get(PENDING_KEY) || "[]") || []; } catch (e) { return []; }
};
const savePending = (events) => store.set(PENDING_KEY, JSON.stringify(events.slice(-PENDING_MAX)));

/** 페이지가 닫혀도 가는 전송 — 로그인 헤더를 실을 수 있는 fetch keepalive, 안 되면 sendBeacon(헤더 없음). */
const sendOnLeave = (body) => {
  const url = `${process.env.REACT_APP_API_BASE_URL || ""}${ENDPOINT}`;
  const json = JSON.stringify(body);
  // keepalive 본문은 브라우저 한도(64KB) 안이어야 한다 — 100건 배치는 대개 20KB 안팎.
  if (typeof fetch === "function" && json.length < 60000) {
    const headers = { "Content-Type": "application/json" };
    let token = null;
    try { token = tokenStore.getAccessToken(); } catch (e) { token = null; }
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      fetch(url, { method: "POST", headers, body: json, keepalive: true }).catch(() => {});
      return true;
    } catch (e) { /* 아래 sendBeacon */ }
  }
  return navigator.sendBeacon ? navigator.sendBeacon(url, new Blob([json], { type: "application/json" })) : false;
};

/** 지금 쌓인 것을 보낸다. beacon=true 면 페이지가 닫혀도 가는 방식(응답 없음). */
export function flush(beacon = false) {
  if (timer) { clearTimeout(timer); timer = null; }
  const events = readPending().concat(queue);
  queue = [];
  savePending([]);
  if (!events.length) return;
  for (let i = 0; i < events.length; i += 100) {
    const body = { ctx: ctx(), events: events.slice(i, i + 100) };
    try {
      if (beacon && sendOnLeave(body)) continue;
      callPostAppEvents(body).catch(() => savePending(readPending().concat(body.events)));
    } catch (e) {
      savePending(readPending().concat(body.events));
    }
  }
}

/** 이벤트 하나. name 은 서버 화이트리스트(screen_view·screen_leave·click·landing·store_click …). */
export function track(name, fields = {}) {
  try {
    queue.push({ name, ts: Date.now(), ...fields });
    getSid(); // 활동 시각 갱신
    if (queue.length >= FLUSH_AT) flush();
    else if (!timer) timer = setTimeout(() => flush(), FLUSH_MS);
  } catch (e) { /* noop */ }
}

/* ───────── 화면·체류·스크롤 ───────── */
const view = { screen: null, startedAt: 0, visibleMs: 0, maxScroll: 0 };
let lastScreen = null; // 앱이 내려갔다 돌아오면 같은 화면을 다시 '진입'으로 센다

const scrollPct = () => {
  const one = (top, height, client) => (height > client ? Math.min(100, Math.round(((top + client) / height) * 100)) : 100);
  const doc = document.documentElement;
  let pct = one(window.scrollY || doc.scrollTop || 0, doc.scrollHeight, window.innerHeight);
  // 일부 화면은 window 대신 .container 가 스크롤된다(ScrollToTop 참고).
  document.querySelectorAll(".container, .scroll-page").forEach((el) => {
    if (el.scrollHeight > el.clientHeight + 4) pct = Math.max(pct, one(el.scrollTop, el.scrollHeight, el.clientHeight));
  });
  return pct;
};

let scrollRaf = 0;
const onScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = 0;
    try { view.maxScroll = Math.max(view.maxScroll, scrollPct()); } catch (e) { /* noop */ }
  });
};

// 보이는 동안만 startedAt 이 있다(가려지면 0). document.hidden 으로 거르면 가려지는 순간(visibilitychange)에
//  이미 hidden=true 라 그때까지의 시간이 버려져 체류가 0 으로 찍혔다.
const visibleSoFar = () => view.visibleMs + (view.startedAt ? Date.now() - view.startedAt : 0);

const leave = () => {
  if (!view.screen) return;
  track("screen_leave", { screen: view.screen, value: visibleSoFar(), scrollPct: view.maxScroll });
};

/**
 * 화면 진입 — AppRouter 가 라우트가 바뀔 때마다 부른다. screen 은 라우트 패턴(/teachers/:id).
 *  via: 어떻게 왔는지(tab 클릭 직후면 'tab', 아니면 'link').
 */
let nextVia = null;
export function trackScreen(screen) {
  try {
    if (!screen || screen === view.screen) return;
    const prev = view.screen;
    leave();
    view.screen = screen;
    lastScreen = screen;
    view.startedAt = Date.now();
    view.visibleMs = 0;
    view.maxScroll = 0;
    track("screen_view", { screen, prevScreen: prev, props: nextVia ? { via: nextVia } : undefined });
    nextVia = null;
    // 첫 화면 높이가 화면보다 짧으면 이미 끝까지 본 것.
    setTimeout(onScroll, 1200);
  } catch (e) { /* noop */ }
}

/** 탭·메뉴·버튼 클릭. target 은 'tab:lessons', 'cta:quote_request' 처럼 '종류:이름'. */
export function trackClick(target, { targetId, position, props } = {}) {
  if (!target) return;
  if (String(target).startsWith("tab:")) nextVia = "tab";
  track("click", { target, targetId: targetId != null ? String(targetId) : undefined, position, props });
}

/** 웹 첫 진입 — 광고 UTM·외부 referrer 가 있을 때만. */
export function trackLanding() {
  try {
    const u = utmCtx();
    const ref = document.referrer || "";
    const external = ref && !/(^|\.)muyeon\.co\.kr/i.test((() => { try { return new URL(ref).hostname; } catch (e) { return ""; } })());
    if (!Object.keys(u).length && !external) return;
    track("landing", { screen: window.location.pathname, referrer: external ? ref : undefined, utm: u });
  } catch (e) { /* noop */ }
}

let started = false;
/** 앱 시작 때 한 번 — 스크롤·가시성·페이지 닫힘 감지. */
export function startAppAnalytics() {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("scroll", onScroll, { passive: true, capture: true });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      view.visibleMs = visibleSoFar();
      view.startedAt = 0;
      // 내려갈 때의 화면이 이탈 후보 — 체류·스크롤을 지금까지로 보내 둔다(돌아오면 새 view 로 이어진다).
      leave();
      flush(true);
      view.screen = null;
    } else if (lastScreen) {
      nextVia = "resume";
      trackScreen(lastScreen);
    }
  });
  window.addEventListener("pagehide", () => { leave(); view.screen = null; flush(true); });
  // data-track="cta:..." 이 붙은 요소 — 화면마다 핸들러를 고치지 않고 속성만 붙인다.
  document.addEventListener("click", (e) => {
    const el = e.target?.closest?.("[data-track]");
    if (el) trackClick(el.getAttribute("data-track"), { targetId: el.getAttribute("data-track-id") || undefined });
  }, true);
  const pending = readPending();
  if (pending.length) flush();
}
